/**
 * Drives a policy document shown in a <dialog>, in one of two ways depending
 * on which markup the dialog carries:
 *
 *   - A `[data-policy-accept]` checkbox: blocks/apply-form.liquid only. No
 *     read-through requirement — ticking the box is the whole gesture, and it
 *     mirrors onto the outer consent switch. See the `accept` branch below.
 *   - The sentinel/agree-button read-through gate, everywhere else:
 *     sections/member-intake.liquid and blocks/membership-purchase.liquid.
 *     Full reasoning — the four ways a scroll gate fails, and why this
 *     watches a sentinel rather than a scroll position — is beside their
 *     markup.
 *
 * Which branch runs is decided purely by markup, per dialog instance, so one
 * page can never accidentally run the other surface's gate.
 *
 * FAILS OPEN. The consent checkbox ships ENABLED from the server and is
 * disabled here (legacy branch only). If this module never loads, the consent
 * still works and the form still submits: the server records the acceptance
 * either way, and this is a deliberateness device, not a security control.
 *
 * Its own file rather than part of apply-form.js because Theme Check caps a
 * single asset's size, and the form module is already near it.
 */
/**
 * @param {HTMLDialogElement} dialog
 * @param {ParentNode} root the form (or document) holding the consent control
 */
function wire(dialog, root) {
  /** @param {string} name */
  const q = (name) =>
    /** @type {HTMLElement | null} */ (dialog.querySelector(`[data-policy-${name}]`));

  const link = /** @type {HTMLAnchorElement | null} */ (root.querySelector('[data-policy-link]'));
  const scroll = q('scroll');
  const end = q('end');
  const agree = /** @type {HTMLButtonElement | null} */ (q('agree'));
  const close = q('close');
  const hint = q('hint');
  const terms = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-policy-consent]'));
  // Named `acceptBox`, not `accept` — the legacy branch below already
  // declares an `accept(check)` function in this same scope, and shadowing it
  // is exactly the kind of bug this restructure must not introduce.
  const acceptBox = /** @type {HTMLInputElement | null} */ (q('accept'));

  // THE CHECKBOX PATH. Identified purely by markup: this dialog carries
  // `[data-policy-accept]` instead of the sentinel/agree-button pair below.
  // member-intake.liquid and membership-purchase.liquid never render that
  // element, so `acceptBox` is always null there and both fall straight
  // through to the unchanged legacy branch beneath this one — nothing here
  // can change their behaviour.
  //
  // No read-through requirement: ticking the box IS the acceptance. It
  // mirrors onto `[data-policy-consent]` when that control exists and closes
  // the dialog. FAILS OPEN the same way the legacy branch does — if `terms`
  // cannot be found (see blocks/apply-form.liquid, where it is only marked
  // `data-policy-consent` while the old `use_agreements` ceremony is off) the
  // checkbox still works, it simply has nothing else to drive yet.
  if (acceptBox) {
    if (typeof dialog.showModal !== 'function') return;

    // Every way in opens the policy with the box UNTICKED, so ticking it is always a
    // fresh act: after someone switches consent off again, the box inside would
    // otherwise still be ticked and a second tick would do nothing.
    const open = () => {
      acceptBox.checked = false;
      if (!dialog.open) dialog.showModal();
      // Start at the top every time: a reopened dialog otherwise keeps the scroll
      // position it was closed at, so the policy appears to begin halfway down.
      q('scroll')?.scrollTo(0, 0);
    };

    link?.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      open();
    });

    // THE SWITCH CANNOT BE TURNED ON BY HAND. Clicking it, or pressing Space on it,
    // opens the policy instead, and only the box inside the dialog turns it on. By
    // the time this runs a click that would turn it ON has already set `checked`,
    // so cancelling the event puts it back to off (the browser reverts a cancelled
    // checkbox click). Turning it OFF is always allowed. Decided 2026-09-30: the
    // earlier "tickable without opening it" let someone record "I have read and
    // accept" without the policy ever being shown. Programmatic changes (the box
    // below) fire no click, so they are untouched. If this module never loads, the
    // switch stays tickable and the server still records the acceptance.
    terms?.addEventListener('click', (event) => {
      if (!terms.checked) return;
      event.preventDefault();
      open();
    });

    close?.addEventListener('click', () => dialog.close());

    acceptBox.addEventListener('change', () => {
      if (!acceptBox.checked) return;
      if (terms) {
        terms.checked = true;
        terms.dispatchEvent(new Event('change', { bubbles: true }));
      }
      dialog.close();
    });
    return;
  }

  if (!link || !scroll || !end || !agree || !terms) {
    // A MIS-SCOPE MUST NOT BE SILENT. Nothing here ships `disabled`, so if the
    // control cannot be found the checkbox stays ENABLED and the read-through
    // gate simply disappears -- on the $30,000 purchase that is a lost consent
    // record with no visual difference. Say so.
    console.warn('[policy-gate] not wired: a control is outside the dialog scope', { link: !!link, terms: !!terms, scroll: !!scroll, end: !!end, agree: !!agree });
    return;
  }
  if (typeof dialog.showModal !== 'function' || typeof IntersectionObserver === 'undefined') return;

  terms.disabled = true;

  // SAY WHY IT IS LOCKED. A disabled tick box with no explanation is a control
  // that looks broken — somebody clicks it, nothing happens, and nothing
  // connects it to the link two words away. Written by the script, so it never
  // appears when there is no gate to explain.
  const consentHint = root.querySelector('[data-policy-consent-hint]');
  const LOCKED_TEXT = 'Read the Privacy Policy to the end to accept.';
  if (consentHint) consentHint.textContent = LOCKED_TEXT;

  // Did this opening start from the CONSENT CONTROL, or from the policy link?
  // The two mean different things on close: someone who clicked the tick box
  // has declared an intent to accept and only owes the read-through, so the box
  // is ticked for them when they finish. Someone who clicked "Privacy Policy"
  // asked to READ it, and ticking their consent because they closed a document
  // would be putting an acceptance in their mouth. Reset every time so the
  // intent never carries over from a previous opening.
  let openedFromConsent = false;

  // Clicking the locked control opens the policy rather than doing nothing.
  // The label is the target because a disabled input fires no events itself —
  // and note that is ALSO why .apply__checkbox:disabled sets
  // `pointer-events: none` in blocks/apply-form.liquid. Without it the disabled
  // box is not a click target at all, the event never reaches this listener,
  // and clicking the box does nothing while clicking the words works. That was
  // live behaviour until 2026-08-20; the comment there records the measurement.
  const label = terms.closest('label');
  label?.addEventListener('click', (event) => {
    if (!terms.disabled) return;

    // LINKS INSIDE THE LABEL ARE NOT THE CONTROL. Two faults came from this
    // handler treating every click on the label as a click on the tick box,
    // and both were live:
    //
    //   1. The PRIVACY link has no `data-policy-link`, so nothing else handles
    //      it — preventDefault here simply swallowed it and the visitor could
    //      not reach the privacy policy at all while the consent was locked.
    //   2. The TERMS link DOES have one, and its own listener below already
    //      calls showModal(). This handler then called showModal() a second
    //      time on an open dialog, which throws InvalidStateError.
    //
    // Anchors are left alone: the gated one is handled where it is declared,
    // and an ordinary one is a link.
    const target = event.target;
    if (target instanceof Element && target.closest('a')) return;

    // A modifier click is "open this elsewhere", never "operate the control".
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    openedFromConsent = true;
    dialog.showModal();
    // NOT `scroll.focus()`. Focusing it programmatically made Chrome paint a
    // focus ring around the whole policy for EVERY visitor, mouse users
    // included — reported as an ugly outline. The scroller keeps tabindex="0",
    // so a keyboard user still reaches it with one Tab and still gets the ring
    // then, which is the only time it carries information. The dialog itself
    // takes focus natively, so Escape and focus containment are unaffected.
  });

  /**
   * A COLLAPSED CONTAINER IS NOT "READ TO THE END".
   *
   * This guard exists because of a real failure, not a hypothetical. The dialog
   * used to sit INSIDE the application <form>, and that form is hidden for
   * anyone who has already applied. A hidden ancestor gives the scroller
   * `clientHeight: 0` and `scrollHeight: 0`, and a zero-height root makes the
   * sentinel trivially intersect — so the observer fired the instant the dialog
   * "opened" and the consent unlocked by itself. Reported from the live site as
   * the agree button being always clickable.
   *
   * The dialog now lives outside the form, so the specific cause is gone. This
   * stays because the class of fault is worth refusing outright: any future
   * layout that collapses this box would silently reopen the same hole, and a
   * consent control that can open itself is the worst possible failure here.
   */
  const readable = () => scroll.clientHeight > 0 && scroll.scrollHeight > 0;

  const unlock = () => {
    if (!agree.disabled) return;
    if (!readable()) return;
    agree.disabled = false;
    if (hint) hint.textContent = '';
    if (consentHint) consentHint.textContent = '';
    observer.disconnect();
    scroll.removeEventListener('scroll', onScroll);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) unlock();
    },
    { root: scroll, threshold: 0 }
  );
  observer.observe(end);

  /**
   * BELT AND BRACES, NOT A REPLACEMENT. The sentinel is the primary path and
   * the only one that answers correctly for a policy too short to scroll, for
   * a zoom that creates new overflow, or for a reader moving a virtual cursor.
   * But an IntersectionObserver is suspended while a tab is not being rendered,
   * and is throttled in some embedded contexts, and a consent control that can
   * get stuck locked is worse than one with a second way to open. Reaching the
   * bottom by scrolling satisfies the same question the sentinel asks.
   *
   * The 4px tolerance absorbs sub-pixel rounding at fractional zoom levels,
   * where scrollTop + clientHeight lands just short of scrollHeight forever.
   */
  const onScroll = () => {
    if (scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 4) unlock();
  };
  scroll.addEventListener('scroll', onScroll, { passive: true });

  link.addEventListener('click', (event) => {
    // Modifier-clicks keep their normal meaning: a new tab stays a new tab.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    dialog.showModal();
    // The box has a size only once it is open. If the policy genuinely does not
    // overflow, this is the honest immediate unlock; if it does, nothing
    // happens and the reader scrolls.
    if (readable() && scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 4) unlock();
  });

  close?.addEventListener('click', () => dialog.close());

  /** @param {boolean} check whether to also tick the box, not merely unlock it */
  const accept = (check) => {
    terms.disabled = false;
    if (check) terms.checked = true;
    // A scripted checkbox does not fire `change`; validity watchers need it.
    terms.dispatchEvent(new Event('change', { bubbles: true }));
    if (consentHint) consentHint.textContent = '';
  };

  agree.addEventListener('click', () => {
    accept(true);
    dialog.close();
    terms.focus();
  });

  /**
   * CLOSING THE POLICY IS NOT A DEAD END.
   *
   * Two faults were reachable here before this listener existed, and both left
   * the visitor stuck with no way forward that they could see:
   *
   *   1. Read the whole policy, then dismiss with Escape or the X instead of
   *      the Agree button, and the tick box stayed DISABLED — permanently, from
   *      the visitor's point of view, since nothing on the page said to reopen
   *      the dialog and press a different button. The requirement had been met
   *      and the control still refused.
   *   2. Clicking the box opened the policy, which is now the documented
   *      behaviour, but closing it returned them to an unticked box. The click
   *      that started the whole interaction produced nothing.
   *
   * So: once the read-through is satisfied (`agree` enabled is exactly that
   * condition, set by the sentinel or the scroll fallback), closing the dialog
   * UNLOCKS the box so the visitor can tick it themselves.
   *
   * IT NO LONGER TICKS IT FOR THEM. Closing the dialog used to count as
   * agreeing whenever the opening began at the consent control — read to the
   * end, dismiss with Escape or the X, and consent was recorded without the
   * person ever pressing Agree. Joey, 2026-08-21: "if i just close the box it
   * counts it as agreeing... need to explicitly click the i agree button."
   *
   * That reasoning was defensible when this only gated a waitlist form. It is
   * not defensible now that the same module gates a $30,000 purchase: a
   * recorded acceptance has to come from an affirmative act, and dismissing a
   * document is closer to declining it than to accepting it. Unlocking is the
   * right half to keep — it removes the dead end where a met requirement still
   * refused — and the tick is the half that has to be earned.
   *
   * No dead end results: the label handler above only intercepts while the box
   * is DISABLED, so once unlocked a click ticks it normally.
   *
   * Dismissing early still changes nothing at all — the gate is not satisfied,
   * so neither branch runs.
   */
  dialog.addEventListener('close', () => {
    if (!agree.disabled) accept(false);
    openedFromConsent = false;
  });
}

// SCOPE TO THE NEAREST CONTAINER, NOT THE DOCUMENT. The comment this replaces
// claimed "the consent control and the dialog are siblings inside the form".
// They never were: all three hosts place the dialog OUTSIDE the form, so
// closest('form') was null in every case and the root was always `document`.
// With two policy-gated blocks on one page -- reachable from the editor, since
// both blocks have presets and Gateway accepts any theme block -- reading the
// second dialog unlocked the FIRST form's checkbox and opened the wrong
// document. Resolve within an explicit scope if one is declared, else the
// dialog's parent, and fall back to document only when there is nothing else.
document.querySelectorAll('[data-policy-dialog]').forEach((el) => {
  const dialog = /** @type {HTMLDialogElement} */ (el);
  const scope = dialog.closest('[data-policy-scope]') || dialog.parentElement;
  if (!scope) return;
  wire(dialog, scope);
});

export {};
